import { createContext, useContext } from 'react';
import { IQuestionnaireResponse } from '../../unsw-fhir/QuestionnaireResponse';
import {
  IQuestionnaire,
  QuestionnaireItemComponent,
  IQuestionnaireItemGroupComponent,
} from '../../unsw-fhir/Questionnaire';
import { isQuestionnaire } from '../../unsw-fhir/Questionnaire/typeGuards';
import { SubmissionErrors, FormApi } from 'final-form';

export interface AnyObject {
  [key: string]: any;
}

export type ISectionItem = {
  linkId: string;
  text: string;
  active?: boolean;
  complete?: boolean;
  enabled?: boolean;
};

export interface IQuestionnaireContext {
  onSubmit: (
    values: AnyObject,
    form: FormApi<AnyObject>,
    callback?: (errors?: SubmissionErrors) => void,
  ) =>
    | SubmissionErrors
    | Promise<SubmissionErrors | undefined>
    | undefined
    | void;
  onComplete: () => void;
  isEnabled: (linkId: string) => boolean;
  isComplete: (linkId?: string) => boolean;
  onNext: () => void;
  onPrevious: () => void;
  setActive: (linkId: string) => void;
  last?: boolean;
  first?: boolean;
  questionnaire: IQuestionnaire;
  response?: IQuestionnaireResponse;
  progress: string;
  complete?: boolean;
  submitting: boolean;
  sections: ISectionItem[];
  itemView: IQuestionnaire | QuestionnaireItemComponent | null;
  itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire | null;
  itemIndex: number;
}

export const QuestionnaireContext = createContext<IQuestionnaireContext>({
  progress: '0',
  submitting: false,
  isComplete: () => false,
  isEnabled: () => false,
  onComplete: () => {},
  onNext: () => {},
  onPrevious: () => {},
  onSubmit: () => {},
  sections: [],
  questionnaire: {
    resourceType: 'Questionnaire',
    status: 'retired',
    item: [],
  },
  setActive: () => {},
  itemView: null,
  itemIndex: -1,
  itemNode: null,
});

export const useSectionsList = () => {
  const context = useContext(QuestionnaireContext);
  const activeId = useActiveId();
  return context.sections.map(x => ({
    ...x,
    active: x.linkId === activeId,
    enabled: context.isEnabled(x.linkId),
    complete: context.isComplete(x.linkId),
  }));
};

export const useActiveId = () => {
  const context = useContext(QuestionnaireContext);
  if (!context.itemView) return '-1';
  if (isQuestionnaire(context.itemView)) return '-1';
  return context.itemView.linkId;
};

export const useSetActive = () => {
  const context = useContext(QuestionnaireContext);
  return context.setActive;
};

export const useSubmitting = () => {
  const context = useContext(QuestionnaireContext);
  return context.submitting;
};

export const useComplete = () => {
  const context = useContext(QuestionnaireContext);
  return context.onComplete;
};
